import React, { useEffect, useState, useRef } from 'react';
import { Box, Alert, Button, Grid, Paper, Typography, FormGroup, FormControlLabel, Checkbox, SvgIcon } from '@mui/material';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import html2canvas from 'html2canvas';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  ResponsiveContainer, 
  Tooltip as RechartsTooltip,
  CartesianGrid
} from 'recharts';

function PokerChipIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      {/* Outer ring */}
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
      
      {/* Inner ring */}
      <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
      
      {/* Center circle */}
      <circle cx="12" cy="12" r="2" />
    </SvgIcon>
  );
}

function StatsPanel({ tournaments, handleDownload }) {
  const currentYear = new Date().getFullYear();
  
  const stats = tournaments.reduce((acc, tournament) => {
    const tournamentYear = new Date(tournament.date).getFullYear();
    
    if (tournamentYear === currentYear) {
      return {
        totalTournaments: acc.totalTournaments + 1,
        totalBuyins: acc.totalBuyins + Number(tournament.buyIn || 0),
        totalRebuys: acc.totalRebuys + Number(tournament.rebuys || 0),
        totalAddons: acc.totalAddons + Number(tournament.addOns || 0),
        totalWinnings: acc.totalWinnings + Number(tournament.winnings || 0),
        totalProfit: acc.totalProfit + Number(tournament.profit || 0)
      };
    }
    return acc;
  }, {
    totalTournaments: 0,
    totalBuyins: 0,
    totalRebuys: 0,
    totalAddons: 0,
    totalWinnings: 0,
    totalProfit: 0
  });

  const formatCurrency = (amount) => {
    return `R${amount.toLocaleString()}`;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2 
      }}>
        <Typography variant="h6">
          {currentYear} Statistics
        </Typography>
        <Button
          className="no-export"
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download Chart
        </Button>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PokerChipIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Games Played</Typography>
            </Box>
            <Typography variant="h4">{stats.totalTournaments}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Total Buy-ins</Typography>
            </Box>
            <Typography variant="h4">{formatCurrency(stats.totalBuyins)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Total Rebuys</Typography>
            </Box>
            <Typography variant="h4">{formatCurrency(stats.totalRebuys)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Total Add-ons</Typography>
            </Box>
            <Typography variant="h4">{formatCurrency(stats.totalAddons)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Total Winnings</Typography>
            </Box>
            <Typography variant="h4">{formatCurrency(stats.totalWinnings)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Total Profit/Loss</Typography>
            </Box>
            <Typography 
              variant="h4" 
              sx={{ 
                color: stats.totalProfit >= 0 ? 'success.main' : 'error.main'
              }}
            >
              {formatCurrency(stats.totalProfit)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function BankrollChart() {
  const { currentUser } = useAuth();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({
    cashGames: 0,
    tournaments: 0,
    wonSessions: 0,
    totalSessions: 0
  });
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const statsAndChartRef = useRef(null);
  const [filters, setFilters] = useState({
    cash: true,
    tournament: true
  });
  const [yAxisConfig, setYAxisConfig] = useState({
    ticks: [0],
    domain: [0, 1000],
    tickCount: 1
  });

  const handleFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      [event.target.name]: event.target.checked
    }));
  };

  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'tournaments'),
      where('userId', '==', currentUser.uid),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tournamentsData = [];
      let runningTotal = 0;
      const chartData = [];
      let cashGames = 0;
      let tournaments = 0;
      let wonSessions = 0;

      const allData = [];
      querySnapshot.forEach((doc) => {
        const tournament = doc.data();
        allData.push({ id: doc.id, ...tournament });
      });

      allData.sort((a, b) => new Date(a.date) - new Date(b.date));

      const filteredData = allData.filter(tournament => {
        // If neither type is selected, return no data
        if (!filters.cash && !filters.tournament) return false;  // Changed from true to false
        
        const isCash = tournament.gameType?.includes('CASH');
        const isTournament = !isCash;
        
        if (filters.cash && isCash) return true;
        if (filters.tournament && isTournament) return true;
        
        return false;
      });

      filteredData.forEach((tournament) => {
        tournamentsData.push({ id: tournament.id, ...tournament });
        runningTotal += tournament.profit;
        
        if (tournament.gameType?.includes('CASH')) {
          cashGames++;
        } else {
          tournaments++; // This is an MTT
        }
        if (tournament.profit > 0) wonSessions++;

        chartData.push({
          date: tournament.date,
          profit: runningTotal,
          sessionProfit: tournament.profit
        });
      });

      // Get min and max values
      const maxProfit = Math.max(...chartData.map(d => d.profit));
      const minProfit = Math.min(...chartData.map(d => d.profit));
      const buffer = Math.max(Math.abs(maxProfit), Math.abs(minProfit)) * 0.1; // 10% buffer

      // Round to nearest 1000 for both min and max
      const maxYValue = Math.ceil((maxProfit + buffer) / 1000) * 1000;
      const minYValue = Math.floor((minProfit - buffer) / 1000) * 1000;
      
      // Generate ticks in increments of 1000 from min to max
      const yAxisTicks = [];
      for (let i = minYValue; i <= maxYValue; i += 1000) {
        yAxisTicks.push(i);
      }

      setTournaments(tournamentsData);
      setData(chartData);
      setStats({
        cashGames,
        tournaments,
        wonSessions,
        totalSessions: cashGames + tournaments
      });
      setYAxisConfig({
        ticks: yAxisTicks,
        domain: [minYValue, maxYValue],
        tickCount: yAxisTicks.length
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, filters]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const date = new Date(label);
      const formattedDate = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
      const profitValue = payload[0].value;
      const sessionValue = payload[0].payload.sessionProfit;
      
      return (
        <Paper 
          elevation={6}
          sx={{ 
            p: 2, 
            backgroundColor: '#1E1E1E',
            border: '2px solid #4CAF50',
            boxShadow: '0 4px 20px rgba(0,0,0,0.8)',
            minWidth: '180px',
          }}
        >
          <Typography variant="subtitle2" color="#888" gutterBottom>
            {formattedDate}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: profitValue >= 0 ? '#4CAF50' : '#f44336'  // Green if positive, red if negative
              }}
            >
              R{profitValue.toLocaleString()}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: sessionValue >= 0 ? '#4CAF50' : '#f44336'  // Green if positive, red if negative
              }}
            >
              Session: R{sessionValue.toLocaleString()}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const formatYAxis = (value) => {
    if (value === 0) return 'R0';
    return `R${Math.floor(value/1000)}K`;
  };

  const handleDownload = async () => {
    if (statsAndChartRef.current) {
      try {
        // Add style to hide download button during capture
        const style = document.createElement('style');
        style.innerHTML = '.no-export { display: none !important; }';
        document.head.appendChild(style);

        const currentYear = new Date().getFullYear();
        const canvas = await html2canvas(statsAndChartRef.current, {
          backgroundColor: '#121212',
          scale: 2,
        });
        
        // Remove the style after capture
        document.head.removeChild(style);
        
        const downloadLink = document.createElement('a');
        downloadLink.href = canvas.toDataURL('image/png');
        downloadLink.download = `poker-bankroll-${currentYear}-statistics-${new Date().toISOString().split('T')[0]}.png`;
        downloadLink.click();
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
  };

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading statistics...
        </Alert>
      )}
      <Box ref={statsAndChartRef}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.cash}
                  onChange={handleFilterChange}
                  name="cash"
                  defaultChecked
                />
              }
              label="Cash Games"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.tournament}
                  onChange={handleFilterChange}
                  name="tournament"
                  defaultChecked
                />
              }
              label="Tournament Games"
            />
          </FormGroup>
        </Box>
        {(filters.cash || filters.tournament) && (
          <>
            <StatsPanel tournaments={tournaments} handleDownload={handleDownload} />
            <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={data} 
                    margin={{ 
                      top: 20,
                      right: 30,
                      left: 60,
                      bottom: 60 
                    }}
                    style={{ backgroundColor: '#1E1E1E' }}
                  >
                    <CartesianGrid 
                      strokeDasharray="3 3" 
                      stroke="#333" 
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis 
                      dataKey="date" 
                      stroke="#666"
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis 
                      stroke="#666"
                      tickFormatter={formatYAxis}
                      ticks={yAxisConfig.ticks}
                      domain={yAxisConfig.domain}
                      tickCount={yAxisConfig.tickCount}
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip />}
                      cursor={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke="#8B5CF6"
                      strokeWidth={2}
                      dot={{
                        r: 4,
                        fill: "#8B5CF6",
                        stroke: "#8B5CF6"
                      }}
                      activeDot={{
                        r: 6,
                        fill: "#8B5CF6",
                        stroke: "#fff",
                        strokeWidth: 2
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#1E1E1E' }}>
                    <Typography variant="h4" color="primary">
                      {stats.totalSessions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SESSIONS
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Cash Game ({stats.cashGames}) • Tournament ({stats.tournaments})
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#1E1E1E' }}>
                    <Typography variant="h4" color="primary">
                      {Math.round((stats.wonSessions / stats.totalSessions) * 100)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      WON
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Won ({stats.wonSessions}) • Lost ({stats.totalSessions - stats.wonSessions})
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}

export default BankrollChart; 